import { CAMPAIGN_OBJECTIVES, type CampaignFilters, type CampaignObjectiveKey } from '@crm/shared';
import { Button, Card, CardContent, Label, RadioGroup, Spinner } from '@crm/ui';
import { List, Plus, Send, Users } from 'lucide-react';
import { useState } from 'react';
import { Autocomplete } from '@/components';
import { useContactLists } from '@/modules/contact-lists/api';
import { ContactListModal } from '@/modules/contact-lists/components/contact-list-modal';
import { useCampaignPreview } from '../api';
import { AudienceFilters } from './audience-filters';
import { RecipientsModal } from './recipients-modal';

interface AudienceTabProps {
  filters: CampaignFilters;
  /** Disparo manual - so os objetivos que esperam o clique passam isto. */
  onDispatch?: () => void;
  dispatching?: boolean;
  /** Quem manda no publico - os filtros abaixo so refinam o que ele decidiu. */
  objective: CampaignObjectiveKey;
  onChange: (filters: CampaignFilters) => void;
  readOnly: boolean;
}

/**
 * Quem recebe. O total aparece junto dos filtros e nao no fim: disparo as
 * cegas e o jeito mais rapido de queimar o numero do salao. Os nomes ficam
 * atras de um botao - a base pode ter milhares.
 *
 * A primeira pergunta e de onde o publico vem: de uma lista que ja existe ou
 * de caracteristicas escolhidas agora. Sem ela, a tela abria com os dois
 * caminhos ligados ao mesmo tempo e nao dava para saber qual estava valendo.
 */
export function AudienceTab({
  filters,
  objective,
  onDispatch,
  dispatching,
  onChange,
  readOnly,
}: AudienceTabProps) {
  const preview = useCampaignPreview(filters, objective, 1, 1);
  const listas = useContactLists();

  const [vendoLista, setVendoLista] = useState(false);
  const [criandoLista, setCriandoLista] = useState(false);
  // Guarda so a escolha da sessao: a campanha grava o recorte, e nao de onde
  // ele veio - escolher uma lista copia os filtros dela.
  const [origem, setOrigem] = useState<'predefinida' | 'dinamica'>('dinamica');
  const [escolhida, setEscolhida] = useState<string | null>(null);

  const doObjetivo = CAMPAIGN_OBJECTIVES.find((item) => item.key === objective);
  const disponiveis = listas.data?.data ?? [];

  return (
    <div className="space-y-4">
      <Card variant="flush">
        <CardContent className="space-y-6 py-6">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <Label>Publico-alvo:</Label>
            <RadioGroup
              inline
              label="Publico-alvo"
              disabled={readOnly}
              options={[
                {
                  value: 'dinamica',
                  label: 'Lista dinamica',
                  hint: 'Escolha as caracteristicas do publico',
                },
                { value: 'predefinida', label: 'Lista de contatos pre-definida' },
              ]}
              value={origem}
              onChange={(proxima) => setOrigem(proxima as 'predefinida' | 'dinamica')}
            />
          </div>

          {origem === 'predefinida' ? (
            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-64 flex-1">
                <Autocomplete
                  label="Qual lista"
                  placeholder="Buscar lista..."
                  emptyLabel="Nenhuma lista escolhida"
                  disabled={readOnly || disponiveis.length === 0}
                  options={disponiveis.map((lista) => ({
                    id: lista.id,
                    label: lista.name,
                    hint: `${lista.total} contatos`,
                  }))}
                  value={
                    escolhida
                      ? [
                          {
                            id: escolhida,
                            label: disponiveis.find((lista) => lista.id === escolhida)?.name ?? '',
                          },
                        ]
                      : []
                  }
                  onChange={(opcoes) => {
                    const lista = disponiveis.find((item) => item.id === opcoes[0]?.id);

                    setEscolhida(lista?.id ?? null);
                    if (lista) onChange(lista.filters);
                  }}
                />
              </div>

              <Button variant="outline" disabled={readOnly} onClick={() => setCriandoLista(true)}>
                <Plus className="h-4 w-4" />
                Criar lista
              </Button>
            </div>
          ) : (
            <AudienceFilters filters={filters} onChange={onChange} readOnly={readOnly} />
          )}
        </CardContent>
      </Card>

      <Card variant="flush">
        <CardContent className="flex flex-wrap items-center gap-3 py-4">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Users className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            {preview.isPending ? (
              <Spinner />
            ) : (
              <p className="text-xl font-semibold">{preview.data?.total ?? 0} destinatarios</p>
            )}
            <p className="text-sm text-muted-foreground">{doObjetivo?.audience}</p>
          </div>

          <div className="ml-auto flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              disabled={(preview.data?.total ?? 0) === 0}
              onClick={() => setVendoLista(true)}
            >
              <List className="h-4 w-4" />
              Ver lista
            </Button>

            {/* Disparar mora junto de para quem: no cabecalho, o botao ficava
                longe do numero que ele manda mensagem. */}
            {onDispatch ? (
              <Button disabled={readOnly || dispatching} onClick={onDispatch}>
                {dispatching ? <Spinner /> : <Send className="h-4 w-4" />}
                Disparar
              </Button>
            ) : null}
          </div>
        </CardContent>
      </Card>

      {vendoLista ? (
        <RecipientsModal
          filters={filters}
          objective={objective}
          onClose={() => setVendoLista(false)}
        />
      ) : null}

      {/* A mesma janela da pagina de listas: quem cria daqui nao aprende um
          segundo jeito de fazer a mesma coisa. */}
      {criandoLista ? (
        <ContactListModal
          filters={filters}
          onClose={() => setCriandoLista(false)}
          onSaved={(lista) => {
            setCriandoLista(false);
            setEscolhida(lista.id);
            onChange(lista.filters);
          }}
        />
      ) : null}
    </div>
  );
}
